import { sum, upperFirst } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Vehicle_TypeEnum, GraphStackResponse } from 'src/api/gen'
import { DatasetCategory } from 'src/commons/chartjs'
import ComparisonPlot, { IComparisonBucket } from 'src/commons/graphs/ComparisonPlot'
import StackedHistogram from 'src/commons/graphs/StackedHistogram'
import TemporalHistogram from 'src/commons/graphs/TemporalHistogram'
import TemporalHistogramWithPreviousYear from 'src/commons/graphs/TemporalHistogramWithPreviousYear'
import TemporalStackedHistogram from 'src/commons/graphs/TemporalStackedHistogram'
import TemporalStackedHistogramWithPreviousYear from 'src/commons/graphs/TemporalStackedHistogramWithPreviousYear'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import {
  IGraphBucketWithTotal,
  IGraphBucketWithTotalWithPreviousYear,
  IGraphCompareWithTotal,
  IGraphData,
  IGraphStack,
  IGraphStackWithPreviousYear,
  IGraphStackWithAverage,
  IGraphCompare,
} from 'src/commons/types'
import { formatNumber, getDocCountSum } from 'src/commons/utils'
import { useAgencySettings } from 'src/layout/AgencySettings'
import HorizontalHistogram from 'src/pages/EmailReport/Graphs/HorizontalHistogram'
import { formatDuration } from 'src/pages/Trips/common'

export function NumberOfTripsPerProviderPerDay(props: IGraphCompareWithTotal) {
  const { t } = useTranslation()
  let title = `${t('kpi.totalTitle')}: ${props.graphReport.total ? formatNumber(props.graphReport.total) : ''}`
  if (props.graphReportPreviousYear) {
    const totalPreviousYear = props.graphReportPreviousYear.total
      ? formatNumber(props.graphReportPreviousYear.total)
      : ''
    title = `${title} (${t('previous year')}: ${totalPreviousYear})`
  }

  return (
    <ComparisonPlot
      title={title}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      rawDataPreviousYear={props.graphReportPreviousYear?.data ? props.graphReportPreviousYear.data : ([] as any)}
      granularity={props.granularity}
      datasetCategory={DatasetCategory.provider}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function NumberOfTripsPerDay(props: IGraphBucketWithTotal) {
  const { t } = useTranslation()
  return (
    <TemporalHistogram
      showTitle
      title={`${t('kpi.totalTitle')}: ${props.graphReport.total ? formatNumber(props.graphReport.total) : ''}`}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      granularity={props.granularity}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function NumberOfTripsPerDayWithPreviousYear(props: IGraphBucketWithTotalWithPreviousYear) {
  const { t } = useTranslation()
  const totalReferenceYear = props.graphReport.total ? formatNumber(props.graphReport.total) : ''
  const totalPreviousYear = props.graphReportPreviousYear.total ? formatNumber(props.graphReportPreviousYear.total) : ''
  return (
    <TemporalHistogramWithPreviousYear
      showTitle
      title={`${t('kpi.totalTitle')}: ${totalReferenceYear} (${t('previous year')}: ${totalPreviousYear})`}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      rawDataPreviousYear={props.graphReportPreviousYear.data ? props.graphReportPreviousYear.data : ([] as any)}
      granularity={props.granularity}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function NumberOfTripsPerZone(props: IGraphData) {
  return (
    <HorizontalHistogram
      labels={props.graphReport.labels}
      datasets={props.graphReport.datasets}
      displayLegend={false}
      enableAnimation={props.enableAnimation}
    />
  )
}

function getLegendForCategories() {
  const { t } = getTranslation()
  return (category: Vehicle_TypeEnum) => upperFirst(t(enumsTranslationKeys.DeviceType[category]))
}

function getTotal(data: GraphStackResponse[]) {
  return sum(
    data.map((item) => {
      return getDocCountSum(item.stackedBy.buckets)
    })
  )
}

export function TripsDuration(props: IGraphStackWithAverage) {
  const average = props.graphReport.average
  const { t } = useTranslation()

  return (
    <StackedHistogram
      showTitle
      title={`${t('kpi.TripsDuration.legend')}: ${formatDuration(average / 60, true)}`}
      rawData={props.graphReport.data}
      formatLabel={getLegendForCategories()}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function PercentageofTripsPerProvider(props: IGraphStack) {
  return (
    <TemporalStackedHistogram
      rawData={props.graphReport}
      formatWithUnit={(amount: string) => `${amount}%`}
      granularity={props.granularity}
      maxValue={100}
      datasetCategory={DatasetCategory.provider}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function PercentageofTripsPerProviderWithPreviousYear(props: IGraphStackWithPreviousYear) {
  return (
    <TemporalStackedHistogramWithPreviousYear
      rawData={props.graphReport}
      rawDataPreviousYear={props.graphReportPreviousYear}
      formatWithUnit={(amount: string) => `${amount}%`}
      granularity={props.granularity}
      maxValue={100}
      datasetCategory={DatasetCategory.provider}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function TotalDistancePerProvider(props: IGraphCompareWithTotal) {
  const { t } = useTranslation()
  const total = props.graphReport.total ? Math.round(props.graphReport.total) : 0
  const { distance_unit } = useAgencySettings()
  return (
    <ComparisonPlot
      showTitle
      title={`${t('kpi.totalTitle')}: ${formatNumber(total)} ${distance_unit}`}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      rawDataPreviousYear={props.graphReportPreviousYear?.data ? props.graphReportPreviousYear.data : ([] as any)}
      granularity={props.granularity}
      datasetCategory={DatasetCategory.provider}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function TotalDistance(props: IGraphBucketWithTotal) {
  const { t } = useTranslation()
  const total = props.graphReport.total ? Math.round(props.graphReport.total) : 0
  const { distance_unit } = useAgencySettings()
  return (
    <TemporalHistogram
      showTitle
      title={`${t('kpi.totalTitle')}: ${formatNumber(total)} ${distance_unit}`}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      granularity={props.granularity}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function TotalDistanceWithPreviousYear(props: IGraphBucketWithTotalWithPreviousYear) {
  const { t } = useTranslation()
  const total = props.graphReport.total ? Math.round(props.graphReport.total) : 0
  const { distance_unit } = useAgencySettings()

  let title = `${t('kpi.totalTitle')}: ${formatNumber(total)} ${distance_unit}`
  if (props.graphReportPreviousYear) {
    const totalPreviousYear = props.graphReportPreviousYear.total ? Math.round(props.graphReportPreviousYear.total) : 0
    title = `${title} (${t('previous year')}: ${formatNumber(totalPreviousYear)} ${distance_unit})`
  }
  return (
    <TemporalHistogramWithPreviousYear
      showTitle
      title={title}
      rawData={props.graphReport.data ? props.graphReport.data : ([] as any)}
      rawDataPreviousYear={props.graphReportPreviousYear.data ? props.graphReportPreviousYear.data : ([] as any)}
      granularity={props.granularity}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function getDataInPercentages(bucket: GraphStackResponse, total: number) {
  return bucket.stackedBy.buckets.map((stackedBucket) => {
    return {
      key: stackedBucket.key,
      doc_count: Math.round((stackedBucket.doc_count / total) * 100),
      doc_count_without_percentage: stackedBucket.doc_count_without_percentage,
    }
  })
}

function formatDataForComparisonPlot(data: GraphStackResponse[], total: number): IComparisonBucket[] {
  const percentageBuckets = data.map((bucket) => {
    const formattedBuckets = getDataInPercentages(bucket, total)
    return {
      compareBy: { buckets: formattedBuckets },
      key: bucket.key,
    }
  })
  return percentageBuckets
}

export function TripsDistribution(props: IGraphStackWithAverage) {
  const { t } = useTranslation()
  const average = props.graphReport.average
  const { distance_unit } = useAgencySettings()
  return (
    <StackedHistogram
      showTitle
      title={`${t('kpi.TripsDistribution.legend')}: ${formatNumber(average)} ${distance_unit}`}
      rawData={props.graphReport.data}
      formatLabel={getLegendForCategories()}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function TripsDistributionComparing(props: IGraphStackWithAverage) {
  const { t } = useTranslation()
  const average = props.graphReport.average
  const total = getTotal(props.graphReport.data)

  const { distance_unit } = useAgencySettings()
  return (
    <ComparisonPlot
      showTitle
      title={`${t('kpi.TripsDistribution.legend')}: ${formatNumber(average)} ${distance_unit}`}
      rawData={formatDataForComparisonPlot(props.graphReport.data, total)}
      formatWithUnit={(amount: string) => `${amount}%`}
      showAverage={false}
      formatLabel={getLegendForCategories()}
      enableAnimation={props.enableAnimation}
    />
  )
}

export function TripsCloseToPublicTransports(props: IGraphCompare) {
  return (
    <ComparisonPlot
      title={props.title}
      rawData={props.graphReport ?? ([] as any)}
      formatWithUnit={(amount: string) => `${amount}%`}
      granularity={props.granularity}
      enableAnimation={props.enableAnimation}
    />
  )
}
